import React, { useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Field } from '@/components/ui/section-card';
import { useLastMeetingUrl, useProviderAppointmentAction } from '@/hooks/use-appointments';
import { useToastStore } from '@/store/toastStore';
import { APPOINTMENT_STATUS, type Appointment } from '@/types/appointment';
import { LOCATION_MODE } from '@/types/location-mode';
import { getApiErrorMessage } from '@/utils/api-error';
import { openAddressInMaps, openMeetingUrl } from '@/utils/appointment-location';

interface Props {
  appointment: Appointment;
}

/**
 * "Onde" da sessão, visto pelo prestador. Online: o link é desta sessão só —
 * ele cola quando tiver (com o último usado como sugestão) e troca quando
 * quiser. Na casa do cliente: o endereço que o cliente informou ao marcar.
 */
export function ProviderAppointmentLocation({ appointment }: Props) {
  if (appointment.locationMode === LOCATION_MODE.ONLINE) {
    return <MeetingLinkField appointment={appointment} />;
  }

  const atClient = appointment.locationMode === LOCATION_MODE.CLIENT_ADDRESS;
  const address = appointment.address?.trim() || null;

  return (
    <View className="gap-2">
      <Field
        label={atClient ? 'Endereço do cliente' : 'Local'}
        value={address}
        emptyText={
          atClient ? 'Cliente ainda não informou — combine direto' : 'Combinado direto com o cliente'
        }
      />
      {atClient && !!address && (
        <PressableScale
          onPress={() => openAddressInMaps(address)}
          className="flex-row items-center justify-center gap-1.5 self-start rounded-full border border-primary/15 bg-primary/5 px-4 py-2"
        >
          <Ionicons name="navigate-outline" size={15} color="#FD5001" />
          <ThemedText className="font-semibold text-sm text-primary">Como chegar</ThemedText>
        </PressableScale>
      )}
    </View>
  );
}

function MeetingLinkField({ appointment }: Props) {
  const current = appointment.meetingUrl?.trim() || null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const { setMeetingUrl } = useProviderAppointmentAction();
  const lastUsed = useLastMeetingUrl(editing && !draft);
  const addToast = useToastStore(s => s.addToast);
  const cancelled = appointment.status === APPOINTMENT_STATUS.CANCELLED;

  const save = (meetingUrl: string | null) =>
    setMeetingUrl.mutate(
      { id: appointment.id, meetingUrl },
      {
        onSuccess: () => {
          setEditing(false);
          addToast(meetingUrl ? 'Link enviado ao cliente.' : 'Link removido.', 'success');
        },
        onError: err => addToast(getApiErrorMessage(err, 'Não foi possível salvar o link.'), 'error'),
      },
    );

  const startEditing = () => {
    setDraft(current ?? '');
    setEditing(true);
  };

  if (!editing) {
    return (
      <View className="gap-2">
        <Field
          label="Link da chamada"
          value={current}
          emptyText="Ainda sem link"
          hint={current ? undefined : 'O cliente vê que você envia o link quando estiver disponível.'}
        />
        {!cancelled && (
          <View className="flex-row flex-wrap gap-2">
            {!!current && (
              <PressableScale
                onPress={() => openMeetingUrl(current)}
                className="flex-row items-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 px-4 py-2"
              >
                <Ionicons name="videocam-outline" size={15} color="#FD5001" />
                <ThemedText className="font-semibold text-sm text-primary">Abrir</ThemedText>
              </PressableScale>
            )}
            <PressableScale
              onPress={startEditing}
              className="flex-row items-center gap-1.5 rounded-full border border-neutral-200 px-4 py-2"
            >
              <Ionicons className="text-neutral-600" name={current ? 'create-outline' : 'link-outline'} size={15} />
              <ThemedText className="font-semibold text-sm text-neutral-700">
                {current ? 'Trocar link' : 'Adicionar link'}
              </ThemedText>
            </PressableScale>
          </View>
        )}
      </View>
    );
  }

  const suggestion = !draft && lastUsed.data && lastUsed.data !== current ? lastUsed.data : null;
  const trimmed = draft.trim();

  return (
    <View className="gap-2">
      <ThemedText className="text-xs text-neutral-500">Link da chamada</ThemedText>
      <Input
        value={draft}
        onChangeText={setDraft}
        placeholder="https://meet.google.com/..."
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="url"
        autoFocus
      />
      {!!suggestion && (
        <PressableScale
          onPress={() => setDraft(suggestion)}
          className="flex-row items-center gap-1.5 self-start rounded-full bg-neutral-100 px-3 py-1.5"
        >
          <Ionicons className="text-neutral-600" name="time-outline" size={13} />
          <ThemedText className="text-xs text-neutral-700" numberOfLines={1}>
            Usar o último: {suggestion}
          </ThemedText>
        </PressableScale>
      )}
      <View className="flex-row flex-wrap gap-2">
        <PressableScale
          disabled={!trimmed || setMeetingUrl.isPending}
          onPress={() => save(trimmed)}
          className={`rounded-full bg-primary px-4 py-2 ${!trimmed ? 'opacity-50' : ''}`}
        >
          <ThemedText className="font-semibold text-sm text-white">Salvar e avisar</ThemedText>
        </PressableScale>
        <PressableScale
          onPress={() => setEditing(false)}
          className="rounded-full border border-neutral-200 px-4 py-2"
        >
          <ThemedText className="font-semibold text-sm text-neutral-700">Cancelar</ThemedText>
        </PressableScale>
        {!!current && (
          <PressableScale
            disabled={setMeetingUrl.isPending}
            onPress={() => save(null)}
            className="rounded-full px-3 py-2"
          >
            <ThemedText className="font-semibold text-sm text-red-600">Remover</ThemedText>
          </PressableScale>
        )}
      </View>
    </View>
  );
}
